import { Button, Dialog, Input, useToast } from './ui';

/**
 * Einen persoenlichen Link verschicken.
 *
 * Der Link wird nur beim Erzeugen angezeigt (gespeichert ist nur eine
 * Pruefsumme). Deshalb gibt es genau hier alle Wege, ihn loszuwerden:
 * WhatsApp - wenn eine Telefonnummer hinterlegt ist, gleich in den Chat -,
 * das Teilen-Fenster des Geraets, E-Mail und Kopieren.
 */
export interface ShareTarget {
  name: string;
  firstName: string;
  url: string;
  phone: string | null;
  email: string | null;
}

/** Telefonnummer fuer wa.me: nur Ziffern, international. Oesterreich als Vorgabe. */
export function whatsappNumber(phone: string | null): string | null {
  if (!phone) return null;
  let digits = phone.replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) digits = digits.slice(1);
  else if (digits.startsWith('00')) digits = digits.slice(2);
  else if (digits.startsWith('0')) digits = `43${digits.slice(1)}`;
  digits = digits.replace(/\D/g, '');
  return digits.length >= 8 ? digits : null;
}

export function shareText(target: ShareTarget): string {
  return `Hallo ${target.firstName}, hier ist dein persönlicher Link für die Rückmeldung zu Trainings und Spielen: ${target.url}\n\nBitte nicht weitergeben – er gilt nur für dich.`;
}

export function ShareActions({ target, compact = false }: { target: ShareTarget; compact?: boolean }): JSX.Element {
  const toast = useToast();
  const text = shareText(target);
  const nummer = whatsappNumber(target.phone);
  const whatsapp = `https://wa.me/${nummer ?? ''}?text=${encodeURIComponent(text)}`;
  const size = compact ? 'sm' : 'md';

  const kopieren = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(target.url);
      toast.show('Link kopiert');
    } catch {
      toast.show('Bitte den Link von Hand kopieren', 'error');
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      <a
        href={whatsapp}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center justify-center rounded-xl bg-[#25D366] font-semibold text-white hover:bg-[#1ebe5b] ${compact ? 'px-3 py-1.5 text-sm' : 'px-4 py-2.5 text-sm'}`}
        title={nummer ? `Direkt an ${target.phone}` : 'Kontakt in WhatsApp auswählen'}
      >
        {nummer ? 'WhatsApp' : 'WhatsApp …'}
      </a>
      {typeof navigator !== 'undefined' && 'share' in navigator && (
        <Button
          size={size}
          variant="secondary"
          onClick={() => void navigator.share({ title: 'Persönlicher Link', text }).catch(() => undefined)}
        >
          Teilen …
        </Button>
      )}
      {target.email && (
        <a
          href={`mailto:${target.email}?subject=${encodeURIComponent('Dein persönlicher Link')}&body=${encodeURIComponent(text)}`}
          className={`inline-flex items-center justify-center rounded-xl font-semibold text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 ${compact ? 'px-3 py-1.5 text-sm' : 'px-4 py-2.5 text-sm'}`}
        >
          E-Mail
        </a>
      )}
      <Button size={size} variant="ghost" onClick={() => void kopieren()}>Kopieren</Button>
    </div>
  );
}

/** Fenster direkt nach dem Erzeugen eines einzelnen Links. */
export function ShareDialog({ target, onClose }: { target: ShareTarget | null; onClose: () => void }): JSX.Element {
  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      title={target ? `Link für ${target.name}` : 'Link'}
      footer={<Button variant="secondary" onClick={onClose}>Fertig</Button>}
    >
      {target && (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Der Link wird nur jetzt angezeigt. Am einfachsten gleich verschicken:
          </p>
          <ShareActions target={target} />
          <Input readOnly value={target.url} aria-label={`Link für ${target.name}`} onFocus={(event) => event.currentTarget.select()} />
          {!target.phone && (
            <p className="hint">Mit einer Telefonnummer bei der Person öffnet „WhatsApp“ direkt den richtigen Chat.</p>
          )}
        </div>
      )}
    </Dialog>
  );
}
